import { aiNotesPath, CAMERAS, type Camera, type FileRef, type Footage } from './footage';
import type { EventInfo } from './reasons';

export interface EventScope {
  readonly kind: 'event';
  readonly folder: string;
  readonly file: string;
  readonly reason: string | undefined;
  readonly trigger: string | undefined;
  readonly cameras: readonly Camera[];
}

export interface FolderScope {
  readonly kind: 'all';
  readonly root: string;
}

export type InstructionScope = EventScope | FolderScope;

export const AI_INSTRUCTIONS_VERSION = 2;

const INTRO = `# Tesla dashcam: write AI notes

Review Tesla dashcam footage and write what happened into a JSON file next to the video. A dashcam viewer reads that file to rank, label and mark the recording, so exact times and honest confidence matter more than prose. Work only from what the frames show.`;

function footageSection(scope: InstructionScope): string {
  if (scope.kind === 'event') {
    const trigger = scope.reason && scope.trigger ? [`- Tesla's reason: ${scope.reason}, triggered at ${scope.trigger} local time`] : [];
    return [
      '## Footage',
      `- Recording: ${scope.folder}`,
      `- Write: ${scope.file}. If that file already exists, leave it as it is and stop.`,
      ...trigger,
      `- Cameras: ${scope.cameras.join(', ')}`,
    ].join('\n');
  }
  return `## Footage
- Folder: ${scope.root}
- Go through every recording that has no notes file yet, oldest first, and skip the ones that have one.
- An event folder holds event.json: write ai.json into that folder.
- Loose clips, as in RecentClips, form one recording per run of clips without a gap longer than 2.5 minutes: write <start of its first clip>.ai.json next to them, for example 2026-05-08_18-41-07.ai.json.`;
}

const PARALLEL = `## Working in parallel
If you can start subagents, for example with Claude Code's Task tool, coordinate and let them do the reviewing:
1. List the recordings that need a notes file under these rules, oldest first, and count them.
2. Give each subagent exactly one recording: these instructions, the recording's folder or clip range, and the file it must write. Tell it to review only that recording and not to start subagents of its own. Never give one recording to two subagents.
3. Run about four at a time unless the person asks for another number, and start the next as soon as one finishes. Extracting and reading frames is heavy, so more at once mostly slows everyone down.
4. Have each subagent use its own temporary folder and reply with one line: the file it wrote, its verdict and its summary. Do not look at frames yourself, so your context lasts the whole run.
5. Check each reported file: it exists, parses as JSON, has "version": ${AI_INSTRUCTIONS_VERSION} and uses only the allowed values. Give a recording that failed or came back invalid to a fresh subagent once; if it fails again, leave it without a file.
6. End with a short report: how many recordings got notes, how many were skipped because they already had notes, which failed, and the incidents and notable recordings by name.
Without subagents, review the recordings yourself, one at a time.`;

const LOOKING = `Clips are named YYYY-MM-DD_HH-MM-SS-<camera>.mp4, last up to a minute and start at the time in their name, in local time. event.json, where present, holds Tesla's trigger time ("timestamp", local time) and its "reason".

## How to look
1. Work in a temporary folder outside the footage and delete it when done. Install ffmpeg first if it is missing (on a Mac: brew install ffmpeg).
2. Motion timeline: for each clip and camera, measure how much the picture changes and print one character per second, so character n (counting from 0) stands for the clip's start plus n seconds, with a space after every tenth:
   ffmpeg -loglevel error -i CLIP -vf "fps=4,scale=160:-1,format=gray,tblend=all_mode=difference,signalstats,metadata=print:key=lavfi.signalstats.YAVG:file=MOTION.txt" -f null -
   awk -F'[:=]' '/pts_time/ {t = int($NF)} /YAVG/ {v = $NF + 0; if (v > m[t]) m[t] = v; if (t > n) n = t} END {for (i = 0; i <= n; i++) {c = "."; if (m[i] >= 1) c = ":"; if (m[i] >= 2) c = "+"; if (m[i] >= 4) c = "*"; if (m[i] >= 8) c = "#"; printf "%s%s", c, (i % 10 == 9 ? " " : "")} print ""}' MOTION.txt
   "." is a still picture, ":" a slight change, "+" movement, "*" strong movement and "#" something large and close. The timelines show where to look and, at the end, whether you looked everywhere.
3. Overview: make one contact sheet per clip and camera, a 10 × 6 grid of one frame per second read left to right, top to bottom, so tile n (counting from 0) shows the clip's start plus n seconds:
   ffmpeg -loglevel error -i CLIP -vf "fps=1,scale=320:-1,tile=10x6" -frames:v 1 SHEET.jpg
   Look at every sheet: someone standing still beside the car barely changes the picture, so the timeline misses them.
4. Close look: wherever something moves within a few metres of the car, starting around Tesla's trigger time where there is one, view that stretch at 4 frames per second, 16 frames to an image:
   ffmpeg -loglevel error -ss OFFSET -t SECONDS -i CLIP -vf "fps=4,scale=480:-1,tile=4x4" CLOSE-%02d.jpg
   Tile k (counting from 0, left to right, top to bottom) of image m (counting from 1) shows the clip's start plus OFFSET + 4 × (m − 1) + k / 4 seconds. For fine detail, such as a hand on the paint, take a single full-size frame:
   ffmpeg -loglevel error -ss TIME -i CLIP -frames:v 1 FRAME.jpg
5. Write down what each image shows as soon as you have looked at it: times, camera, who or what, and how close. In a long review, images seen earlier may no longer be available to you; written notes are.
6. Follow each subject across cameras by clothing or shape, so a person walking past keeps one subject_id. The cameras leave blind spots, usually beside the front corners, so a few seconds can pass between sightings.
7. Before writing, account for every burst in the timelines and everything on the sheets. Afterwards, check that the file parses as JSON and uses only the allowed values.

## What to note
- damage: contact that can mark or dent the car, seen happening in a frame: a key or tool drawn along the paint, a door opened into it, a vehicle or trolley bumping it, a kick, something thrown hitting it. Someone close by, a hand near the paint, a person out of view beside the car or Tesla's trigger on its own is not damage: note an approach instead and say in its label what you could and could not see.
- approach: a person, child, animal, vehicle or cyclist coming within about 3 m, one entry per subject per approach: "start" and "end" when it comes within and goes beyond about 3 m, "peak" at the closest moment seen. Traffic that keeps moving in its lane is not an approach; a vehicle or cyclist that stops alongside, parks or opens a door is. Give each distinct subject its own subject_id (p1, p2, v1, …) and reuse it when the same one comes back, so visits by different people stay apart. Use the distance touching only when a frame shows the contact, such as leaning or sitting on the car.
- change: the moment a still scene starts to change, such as a car parking alongside or someone arriving, so the viewer can start playback there.
- near_miss: while driving, a cut-off, a sudden stop ahead, or a pedestrian or cyclist close in front.
- emergency: police, ambulance or fire vehicles, blue lights, a police stop.
- animal: a cat on the car, a dog at a door, wildlife on the road.
- plate: the licence plate of a vehicle that touches the car, and of no other.

## Verdict
- incident: damage or contact, or a near miss.
- notable: someone stopped at the car, lingered, looked in or reached toward it, a vehicle parked or stopped alongside, or an emergency vehicle.
- routine: traffic, and people walking past without stopping, even close to the car.
- false_alarm: a Sentry or motion trigger with nothing near the car: shadows, light changes, rain, leaves, insects, distant traffic.`;

const FORMAT = `## Format
Write exactly this structure, as valid JSON. Times are local, like Tesla's own, with tenths of a second. Leave out fields that do not apply, and write an empty "moments" list when nothing happened.

{
  "version": ${AI_INSTRUCTIONS_VERSION},
  "model": "name of the AI model",
  "created": "2026-05-08T19:02:11",
  "summary": "One sentence a person can scan in a list.",
  "verdict": "incident",
  "verdict_reason": "Why, in one sentence.",
  "tags": ["person", "keying", "left side"],
  "moments": [
    {
      "kind": "damage",
      "start": "2026-05-08T18:41:30.5",
      "end": "2026-05-08T18:41:34.0",
      "peak": "2026-05-08T18:41:32.0",
      "camera": "left_repeater",
      "subject": "adult",
      "subject_id": "p1",
      "distance": "touching",
      "label": "Man runs a key along the rear door",
      "confidence": 0.8
    }
  ]
}

Allowed values:
- version: always ${AI_INSTRUCTIONS_VERSION}, the version of these instructions
- kind: damage, approach, change, near_miss, emergency, animal
- verdict: incident, notable, routine, false_alarm
- camera: front, back, left_repeater, right_repeater, left_pillar, right_pillar
- subject: adult, child, vehicle, cyclist, animal, unknown
- distance: touching, under_1m, 1_3m, over_3m
- plate: text, only as described above
- label: what happened, under 60 characters
- confidence: a number from 0 to 1 for how clearly the frames show it: 0.9 or more when unmistakable, about 0.7 when clear but small or partly hidden, 0.5 or less when unsure

## Rules
- Describe only what is visible. Do not guess intent or identity; describe people only as far as needed to tell them apart, such as clothing or size.
- Base every entry on what a frame shows and leave out anything you only suspect. Tesla's reason tells you where to look, not what happened: when it reports a bump or motion but no frame shows anything touching the car, write no damage entry and say so in verdict_reason.
- Contact in a blind spot cannot be seen: if someone stays out of view beside the car for longer than walking past would take, say so in the label and lower the confidence.
- Write a notes file only once its recording is fully reviewed: an existing file counts as done.
- Never replace or edit an existing notes file unless the person who gave you these instructions explicitly asks to regenerate, redo or overwrite it.
- When asked to regenerate outdated notes, redo only the files whose "version" is missing or lower than ${AI_INSTRUCTIONS_VERSION}.
- Write one notes file per recording and nothing else into the footage folders.`;

const LOCAL_STAMP_LENGTH = 19;

export function eventScope<F extends FileRef>(footage: Footage<F>, info: EventInfo | undefined): EventScope {
  const slots = footage.clips.map((clip) => clip.slot);
  const present = new Set(footage.clips.map((clip) => clip.camera));
  return {
    kind: 'event',
    folder: footage.eventJson ? footage.dir : `${footage.dir}, clips ${slots[0]} to ${slots.at(-1)}`,
    file: aiNotesPath(footage),
    reason: info?.reason.label,
    trigger: info?.timestamp === undefined ? undefined : new Date(info.timestamp).toISOString().slice(0, LOCAL_STAMP_LENGTH),
    cameras: CAMERAS.filter((camera) => present.has(camera)),
  };
}

export const aiInstructions = (scope: InstructionScope): string =>
  [INTRO, footageSection(scope), ...(scope.kind === 'all' ? [PARALLEL] : []), LOOKING, FORMAT].join('\n\n');
